import { useEffect, useRef } from 'react';
import { useMsal } from '@azure/msal-react';
import { useAppDispatch } from '../store/hooks';
import { setImageQuota } from '../store/chatSlice';
import { createChatService } from '../utils/chat.service';
import logger from '../utils/logger';

const DAILY_LIMIT = 3;
const IMAGE_USAGE_KEY = 'dailyImageUsage';

function cleanResetUtc(raw: string): string {
  try {
    let cleaned = raw.trim().replace(/^"+|"+$/g, '').replace(/\\+/g, '');
    cleaned = cleaned.replace(/^(\d{4}-\d{2}-\d{2}) (\d{2}:\d{2}:\d{2})/, '$1T$2');
    cleaned = cleaned.replace(/(\+00:00| UTC)$/i, 'Z');
    if (/^\d{4}-\d{2}-\d{2}$/.test(cleaned)) cleaned += 'T00:00:00Z';
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/.test(cleaned)) cleaned += 'Z';
    cleaned = cleaned.replace(/(\.\d{3})\d+Z$/, '$1Z');
    cleaned = cleaned.replace(/(\.\d{3})\d+(\+00:00)$/, '$1Z');
    return cleaned;
  } catch {
    return raw;
  }
}

/**
 * Manages image generation quota tracking.
 *
 * Encapsulates localStorage persistence, chat-history-based usage detection,
 * and server quota fetching. Dispatches `setImageQuota` to Redux so that
 * `ImageUsageAlert` can be driven entirely from store state — mirroring
 * how `TranslationQuotaAlert` is handled.
 */
export function useImageQuota({
  chatHistory,
  isImageStreaming,
}: {
  chatHistory: any[];
  isImageStreaming: boolean;
}) {
  const dispatch = useAppDispatch();
  const { instance: msalInstance } = useMsal();

  const chatServiceRef = useRef<ReturnType<typeof createChatService> | null>(null);
  if (!chatServiceRef.current && msalInstance) {
    chatServiceRef.current = createChatService(msalInstance);
  }

  // Internal tracking refs — not exposed to Redux until the alert should appear
  const lastImageMessageCountRef = useRef(0);
  const usageDateRef = useRef<string>('');
  const localCountRef = useRef<number>(0);
  const localLimitRef = useRef<number>(DAILY_LIMIT);
  const serverRemainingRef = useRef<number | undefined>(undefined);
  const serverResetUtcRef = useRef<string | undefined>(undefined);

  // Load existing usage from localStorage on mount (no Redux dispatch — alert not shown on load)
  useEffect(() => {
    try {
      const raw = localStorage.getItem(IMAGE_USAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        const today = new Date().toISOString().slice(0, 10);
        if (parsed.date === today) {
          localCountRef.current = parsed.count || 0;
          usageDateRef.current = parsed.date;
        } else {
          // Different day — reset
          usageDateRef.current = today;
          localStorage.setItem(IMAGE_USAGE_KEY, JSON.stringify({ date: today, count: 0 }));
        }
      } else {
        const today = new Date().toISOString().slice(0, 10);
        usageDateRef.current = today;
        localStorage.setItem(IMAGE_USAGE_KEY, JSON.stringify({ date: today, count: 0 }));
      }
    } catch {
      // Fallback safe initialization
      const today = new Date().toISOString().slice(0, 10);
      usageDateRef.current = today;
    }
  }, []);

  // Fetch quota from server when image generation starts — updates refs only so
  // the most-recent server data is ready when the alert is shown on completion.
  useEffect(() => {
    if (!isImageStreaming) return;
    let cancelled = false;
    const fetchUpdatedQuota = async () => {
      try {
        if (!chatServiceRef.current) return;
        const quotaResp = await chatServiceRef.current.getImageQuota();
        if (cancelled) return;
        if (quotaResp.success && quotaResp.quota) {
          logger.log('QUOTA: ' + JSON.stringify(quotaResp.quota));
          localLimitRef.current = quotaResp.quota.quota_limit;
          localCountRef.current = quotaResp.quota.current_usage;
          serverRemainingRef.current = quotaResp.quota.remaining;
          serverResetUtcRef.current = cleanResetUtc(quotaResp.quota.reset_time);
        }
      } catch {
        /* suppress quota errors */
      }
    };
    fetchUpdatedQuota();
    return () => { cancelled = true; };
  }, [isImageStreaming]);

  // Detect new assistant image messages and dispatch quota state to Redux (triggers alert)
  useEffect(() => {
    const imageMessages = (chatHistory as any[]).filter(
      (m: any) =>
        m.role === 'assistant' &&
        typeof m.content === 'string' &&
        m.content.includes('![Generated Image]')
    );
    if (imageMessages.length > lastImageMessageCountRef.current) {
      const newImages = imageMessages.length - lastImageMessageCountRef.current;
      lastImageMessageCountRef.current = imageMessages.length;
      const today = new Date().toISOString().slice(0, 10);
      if (usageDateRef.current !== today) {
        usageDateRef.current = today;
        localCountRef.current = 0;
      }
      const limit = localLimitRef.current || DAILY_LIMIT;
      const updated = Math.min(localCountRef.current + newImages, limit);
      localCountRef.current = updated;
      try {
        localStorage.setItem(IMAGE_USAGE_KEY, JSON.stringify({ date: usageDateRef.current, count: updated }));
      } catch {}
      dispatch(setImageQuota({
        count: updated,
        limit,
        remaining: serverRemainingRef.current,
        resetUtc: serverResetUtcRef.current,
      }));
    }
  }, [chatHistory, dispatch]);
}
